import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Save, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    tagline: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    background_image: '',
    logo: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/company-profile`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const setUploading = type === 'bg' ? setUploadingBg : setUploadingLogo;
    setUploading(true);

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload);
      if (type === 'bg') {
        setFormData({ ...formData, background_image: response.data.file_id });
      } else {
        setFormData({ ...formData, logo: response.data.file_id });
      }
      toast.success(`${type === 'bg' ? 'Background' : 'Logo'} uploaded`);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/company-profile`, formData);
      toast.success('Company profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-600 text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      <div
        className="relative h-64 bg-cover bg-center"
        style={{
          backgroundImage: formData.background_image
            ? `url(${API}/uploads/${formData.background_image})`
            : 'linear-gradient(135deg, #D97706 0%, #92400E 100%)',
        }}
      >
        <div className="absolute inset-0 bg-black/30"></div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="absolute top-4 left-4 text-white hover:bg-white/20 h-12 w-12 z-10"
          title="Home"
        >
          <Home className="h-6 w-6" />
        </Button>
        <div className="absolute bottom-4 right-4 z-10">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'bg')}
            className="hidden"
            id="bg-upload"
            disabled={uploadingBg}
          />
          <label htmlFor="bg-upload">
            <Button
              type="button"
              variant="secondary"
              disabled={uploadingBg}
              onClick={() => document.getElementById('bg-upload').click()}
              className="cursor-pointer"
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploadingBg ? 'Uploading...' : 'Change Background'}
            </Button>
          </label>
        </div>
        <div className="absolute inset-0 flex flex-col justify-end p-6">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight font-heading">
            Company Profile
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-stone-800 font-heading mb-6">Company Logo</h2>
            <div className="flex items-center gap-6">
              {formData.logo ? (
                <div className="relative">
                  <img
                    src={`${API}/uploads/${formData.logo}`}
                    alt="Company Logo"
                    className="w-32 h-32 rounded-xl object-contain border-2 border-stone-200 bg-white p-2"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, logo: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-xl bg-stone-200 flex items-center justify-center border-2 border-stone-300">
                  <span className="text-stone-500 text-sm">No Logo</span>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'logo')}
                  className="hidden"
                  id="logo-upload"
                  disabled={uploadingLogo}
                />
                <label htmlFor="logo-upload">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={uploadingLogo}
                    onClick={() => document.getElementById('logo-upload').click()}
                    className="cursor-pointer h-14 px-8"
                  >
                    <Upload className="mr-2 h-5 w-5" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-stone-800 font-heading mb-6">Company Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name" className="text-stone-700 font-medium">
                    Company Name
                  </Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="h-14 bg-stone-50 border-stone-200 rounded-xl text-lg mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="tagline" className="text-stone-700 font-medium">
                    Tagline
                  </Label>
                  <Input
                    id="tagline"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    placeholder="e.g., Quality Honey Since 1990"
                    className="h-14 bg-stone-50 border-stone-200 rounded-xl text-lg mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address" className="text-stone-700 font-medium">
                  Address
                </Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-stone-50 border-stone-200 rounded-xl text-lg mt-1 min-h-20"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-stone-700 font-medium">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-14 bg-stone-50 border-stone-200 rounded-xl text-lg mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-stone-700 font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-14 bg-stone-50 border-stone-200 rounded-xl text-lg mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="website" className="text-stone-700 font-medium">
                  Website
                </Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://..."
                  className="h-14 bg-stone-50 border-stone-200 rounded-xl text-lg mt-1"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white h-16 rounded-full text-lg font-bold"
          >
            <Save className="mr-2 h-6 w-6" />
            {saving ? 'Saving...' : 'Save Company Profile'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CompanyProfile;
